/* SuperLikeStore — modal per acquistare Super Like (€ o Karma).
   Cards selezionabili con animazioni. Apre StripePaymentSheet per il pagamento.
*/
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Star, X, Zap, Coins, Loader2, Check, Lock } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import StripePaymentSheet from "@/components/StripePaymentSheet";
import { hapticTap, hapticSuccess } from "@/lib/haptics";

export default function SuperLikeStore({ open, onClose, onPurchased }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("euro"); // "euro" | "karma"
  const [selected, setSelected] = useState(null);
  const [pubKey, setPubKey] = useState("");
  const [intent, setIntent] = useState(null); // {client_secret, payment_intent_id, pack}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get("/match/super-likes/balance"),
      api.get("/payments/config"),
    ]).then(([b, c]) => {
      setData(b.data);
      setPubKey(c.data.publishable_key);
      setSelected(b.data.packs?.[0]?.id || null);
    }).catch(() => toast.error("Errore caricamento"))
      .finally(() => setLoading(false));
  }, [open]);

  const refresh = async () => {
    try {
      const { data: b } = await api.get("/match/super-likes/balance");
      setData(b);
    } catch {}
  };

  const onBuyEuro = async () => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/create-intent", { kind: "super_likes", pack_id: selected });
      setIntent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione pagamento");
    } finally {
      setBusy(false);
    }
  };

  const onBuyKarma = async () => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/match/super-likes/redeem-karma", { pack_id: selected });
      hapticSuccess();
      toast.success(`+${data.balance - (data.balance - data.pack.qty)} Super Like usando ${data.karma_used} karma!`);
      await refresh();
      onPurchased?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore conferma karma");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  const selectedPack = data?.packs?.find((p) => p.id === selected);

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1400] flex items-end sm:items-center justify-center pb-[160px] sm:pb-0"
          data-testid="super-like-store"
        >
          <div className="absolute inset-0 bg-black/45 backdrop-blur-md" onClick={onClose} />

          <motion.div
            initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            className="relative w-full sm:max-w-md mx-3 sm:mx-0 rounded-t-[28px] sm:rounded-[28px] bg-white border border-[#F0EBE3] overflow-hidden"
            style={{ maxHeight: "92dvh", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.30)" }}
          >
            {/* Cinematic header */}
            <div className="relative px-5 pt-6 pb-5">
              <motion.div
                aria-hidden
                className="absolute -top-20 -right-16 h-56 w-56 rounded-full"
                style={{ background: "radial-gradient(circle, rgba(123,97,255,0.30) 0%, transparent 60%)" }}
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 4, repeat: Infinity }}
              />
              <motion.div
                aria-hidden
                className="absolute -top-10 -left-10 h-40 w-40 rounded-full"
                style={{ background: "radial-gradient(circle, rgba(255,122,26,0.30) 0%, transparent 60%)" }}
                animate={{ scale: [1.08, 1, 1.08] }}
                transition={{ duration: 4, repeat: Infinity }}
              />
              <div className="relative flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#7B61FF] inline-flex items-center gap-1.5">
                    <Star className="h-3 w-3 fill-[#7B61FF]" /> Super Like
                  </div>
                  <h2 className="mt-1 font-display text-3xl text-[#1A1A1A] leading-tight">
                    Pacchetti
                  </h2>
                  <div className="mt-1 text-[12px] text-[#6B6B6B]">
                    Hai <span className="font-semibold text-[#1A1A1A]" data-testid="sl-current-balance">{data?.balance ?? 0}</span> Super Like
                    {data?.granted_this_session && <span className="text-[#FF7A1A]"> · +1 gratis mensile aggiunto</span>}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  data-testid="sl-store-close"
                  className="h-9 w-9 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D2] flex items-center justify-center"
                >
                  <X className="h-4 w-4 text-[#6B6B6B]" />
                </button>
              </div>
            </div>

            {/* Tab switcher € vs Karma */}
            <div className="px-5">
              <div className="bg-[#F4EFE6] rounded-full p-1 inline-flex w-full" data-testid="sl-store-tabs">
                {[
                  { id: "euro", label: "Paga in €", icon: Zap },
                  { id: "karma", label: "Usa Karma", icon: Coins },
                ].map((t) => {
                  const active = tab === t.id;
                  const Ic = t.icon;
                  return (
                    <button
                      key={t.id}
                      data-testid={`sl-tab-${t.id}`}
                      onClick={() => { hapticTap(); setTab(t.id); }}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-[13px] font-medium transition-colors"
                      style={{
                        background: active ? "#FFFFFF" : "transparent",
                        color: active ? "#FF7A1A" : "#6B6B6B",
                        boxShadow: active ? "0 4px 12px -4px rgba(0,0,0,0.10)" : "none",
                      }}
                    >
                      <Ic className="h-3.5 w-3.5" /> {t.label}
                    </button>
                  );
                })}
              </div>
              {tab === "karma" && (
                <div className="mt-2 text-[11px] text-[#9A9A9A]">
                  Hai <span className="font-semibold text-[#FF7A1A]">{data?.karma_balance ?? 0} karma</span> · guadagna karma usando ORIO (conferme, missioni, streak).
                </div>
              )}
            </div>

            {/* Packs */}
            <div className="px-5 py-4 space-y-2 overflow-y-auto" style={{ maxHeight: "44dvh" }}>
              {loading ? (
                <div className="text-center py-10 text-[#9A9A9A] inline-flex items-center gap-2 justify-center w-full">
                  <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
                </div>
              ) : (
                (data?.packs || []).map((p, i) => {
                  const isSel = selected === p.id;
                  const cost = tab === "euro" ? `€ ${p.eur.toFixed(2)}` : `${p.karma} karma`;
                  const insufficient = tab === "karma" && (data?.karma_balance || 0) < p.karma;
                  const isBest = i === 1; // middle pack
                  return (
                    <motion.button
                      key={p.id}
                      whileTap={{ scale: 0.985 }}
                      data-testid={`sl-pack-${p.id}`}
                      onClick={() => { hapticTap(); setSelected(p.id); }}
                      disabled={insufficient}
                      className="relative w-full text-left px-4 py-3.5 rounded-2xl border-2 transition-colors flex items-center gap-3"
                      style={{
                        borderColor: isSel ? "#7B61FF" : "#F0EBE3",
                        background: isSel ? "linear-gradient(135deg, rgba(123,97,255,0.06) 0%, rgba(255,122,26,0.05) 100%)" : "#FFFFFF",
                        opacity: insufficient ? 0.55 : 1,
                      }}
                    >
                      <div
                        className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
                        style={{
                          background: isSel ? "linear-gradient(135deg, #7B61FF 0%, #FF7A1A 100%)" : "#F4EFE6",
                        }}
                      >
                        <Star className={`h-5 w-5 ${isSel ? "text-white fill-white" : "text-[#7B61FF]"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display text-[19px] text-[#1A1A1A] leading-none">{p.qty}</span>
                          <span className="text-[12px] text-[#6B6B6B]">Super Like</span>
                          {isBest && <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
                            style={{ background: "linear-gradient(135deg, #FF7A1A, #F4B83C)", color: "white" }}>
                            Più scelto
                          </span>}
                        </div>
                        <div className="mt-0.5 text-[11px] text-[#9A9A9A]">
                          {tab === "euro" ? `€ ${(p.eur / p.qty).toFixed(2)} cad.` : `${Math.round(p.karma / p.qty)} karma cad.`}
                        </div>
                      </div>
                      <div className="font-display text-[18px] tabular-nums shrink-0" style={{ color: isSel ? "#7B61FF" : "#1A1A1A" }}>
                        {cost}
                      </div>
                      {isSel && (
                        <motion.div
                          layoutId="pack-check"
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#7B61FF] text-white flex items-center justify-center"
                          style={{ boxShadow: "0 4px 12px -2px rgba(123,97,255,0.45)" }}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </motion.div>
                      )}
                      {insufficient && (
                        <span className="absolute top-2 right-2 text-[9px] text-[#E94545] uppercase tracking-wider font-semibold inline-flex items-center gap-1">
                          <Lock className="h-2.5 w-2.5" /> manca karma
                        </span>
                      )}
                    </motion.button>
                  );
                })
              )}
            </div>

            {/* CTA */}
            <div className="px-5 pb-5 pt-2 border-t border-[#F0EBE3] bg-[#FBF7F1]">
              {tab === "euro" ? (
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  data-testid="sl-buy-euro"
                  onClick={onBuyEuro}
                  disabled={!selected || busy}
                  className="w-full py-3.5 rounded-full text-white font-semibold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{
                    background: "linear-gradient(135deg, #7B61FF 0%, #FF7A1A 100%)",
                    boxShadow: "0 14px 30px -10px rgba(123,97,255,0.55)",
                  }}
                >
                  {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Apertura…</> : <>
                    <Sparkles className="h-4 w-4" /> Procedi al pagamento · € {selectedPack?.eur?.toFixed(2)}
                  </>}
                </motion.button>
              ) : (
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  data-testid="sl-buy-karma"
                  onClick={onBuyKarma}
                  disabled={!selected || busy || (selectedPack && (data?.karma_balance || 0) < selectedPack.karma)}
                  className="w-full py-3.5 rounded-full text-white font-semibold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{
                    background: "linear-gradient(135deg, #2DBA60 0%, #7B61FF 100%)",
                    boxShadow: "0 14px 30px -10px rgba(45,186,96,0.45)",
                  }}
                >
                  {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Conferma…</> : <>
                    <Coins className="h-4 w-4" /> Riscatta con {selectedPack?.karma} karma
                  </>}
                </motion.button>
              )}
              <div className="mt-2 text-center text-[10.5px] text-[#9A9A9A]">
                Acquisto sicuro · 1 Super Like gratis al mese per gli abbonati Pro
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Stripe sheet — appears above the store */}
      {intent && (
        <StripePaymentSheet
          open={!!intent}
          onClose={() => setIntent(null)}
          clientSecret={intent.client_secret}
          paymentIntentId={intent.payment_intent_id}
          publishableKey={pubKey}
          pack={intent.pack}
          onSuccess={async (res) => {
            setIntent(null);
            await refresh();
            onPurchased?.(res);
            setTimeout(() => onClose?.(), 600);
          }}
        />
      )}
    </>
  );
}
